/**
 * Forwards a query parameter only if it matches specific allowed values.
 * 
 * @param targetUrl - The URL to append the query parameter to
 * @param paramName - The query parameter name to check and forward
 * @param allowedValues - Array of allowed values, or a single allowed value
 * @returns The URL with the parameter appended if it exists and matches allowed values
 * 
 * @example
 * // Current URL: https://example.com?ref=admin_login
 * forwardQueryParamIfMatches('/profile', 'ref', 'admin_login') // Returns: '/profile?ref=admin_login'
 * forwardQueryParamIfMatches('/profile', 'ref', ['admin_login', 'port']) // Returns: '/profile?ref=admin_login'
 * 
 * // Current URL: https://example.com?ref=other_value
 * forwardQueryParamIfMatches('/profile', 'ref', 'admin_login') // Returns: '/profile'
 */
export const forwardQueryParamIfMatches = (
  targetUrl: string,
  paramName: string,
  allowedValues: string | string[]
): string => {
  if (typeof window === 'undefined') return targetUrl;

  const currentParams = new URLSearchParams(window.location.search);
  const paramValue = currentParams.get(paramName);
  
  if (!paramValue) return targetUrl;
  
  const allowed = Array.isArray(allowedValues) ? allowedValues : [allowedValues];
  
  if (!allowed.includes(paramValue)) return targetUrl;
  
  const [baseUrl, queryString] = targetUrl.split('?');
  const targetParams = new URLSearchParams(queryString || '');
  
  if (!targetParams.has(paramName)) {
    targetParams.set(paramName, paramValue);
  }
  
  const newQueryString = targetParams.toString();
  return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
};



